// Firebase Activity Registration System
// Handles activity registration creation, management, and real-time updates

import { 
  doc, 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  onSnapshot,
  writeBatch,
  runTransaction,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './config';
import { COLLECTIONS } from './collections';

// ===== ACTIVITY REGISTRATION MANAGEMENT =====

// Create a new activity registration
export const createActivityRegistration = async (registrationData) => {
  try {
    const registration = {
      ...registrationData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      isActive: true,
      totalRegistrations: 0
    };

    const docRef = await addDoc(collection(db, COLLECTIONS.ACTIVITY_REGISTRATIONS), registration);
    return { success: true, registrationId: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Update activity registration
export const updateActivityRegistration = async (registrationId, updateData) => {
  try {
    const registrationRef = doc(db, COLLECTIONS.ACTIVITY_REGISTRATIONS, registrationId);
    await updateDoc(registrationRef, {
      ...updateData,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Delete activity registration
export const deleteActivityRegistration = async (registrationId) => {
  try {
    // Delete registration and all associated participant registrations
    const batch = writeBatch(db);
    
    // Delete registration
    const registrationRef = doc(db, COLLECTIONS.ACTIVITY_REGISTRATIONS, registrationId);
    batch.delete(registrationRef);
    
    // Delete all participant registrations for this activity
    const participantsQuery = query(
      collection(db, COLLECTIONS.ACTIVITY_PARTICIPANTS),
      where('registrationId', '==', registrationId)
    );
    const participantsSnapshot = await getDocs(participantsQuery);
    participantsSnapshot.forEach((participantDoc) => {
      batch.delete(participantDoc.ref);
    });
    
    await batch.commit();
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get activity registration by ID
export const getActivityRegistration = async (registrationId) => {
  try {
    const registrationRef = doc(db, COLLECTIONS.ACTIVITY_REGISTRATIONS, registrationId);
    const registrationSnap = await getDoc(registrationRef);
    
    if (registrationSnap.exists()) {
      return { success: true, data: { id: registrationSnap.id, ...registrationSnap.data() } };
    } else {
      return { success: false, error: 'Registration not found' };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get all activity registrations
export const getAllActivityRegistrations = async () => {
  try {
    const registrationsQuery = query(
      collection(db, COLLECTIONS.ACTIVITY_REGISTRATIONS),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(registrationsQuery);
    
    const registrations = [];
    snapshot.forEach((doc) => {
      registrations.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, data: registrations };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ===== REAL-TIME REGISTRATION LISTENERS =====

// Listen to activity registration changes
export const listenToActivityRegistration = (registrationId, callback) => {
  const registrationRef = doc(db, COLLECTIONS.ACTIVITY_REGISTRATIONS, registrationId);
  
  const unsubscribe = onSnapshot(registrationRef, (doc) => {
    if (doc.exists()) {
      callback({ success: true, data: { id: doc.id, ...doc.data() } });
    } else {
      callback({ success: false, error: 'Registration not found' });
    }
  }, (error) => {
    callback({ success: false, error: error.message });
  });

  return unsubscribe;
};

// Listen to all activity registrations
export const listenToAllActivityRegistrations = (callback) => {
  const registrationsQuery = query(
    collection(db, COLLECTIONS.ACTIVITY_REGISTRATIONS),
    orderBy('createdAt', 'desc')
  );

  const unsubscribe = onSnapshot(registrationsQuery, (snapshot) => {
    const registrations = [];
    snapshot.forEach((doc) => {
      registrations.push({ id: doc.id, ...doc.data() });
    });
    callback({ success: true, data: registrations });
  }, (error) => {
    callback({ success: false, error: error.message });
  });

  return unsubscribe;
};

// ===== PARTICIPANT REGISTRATION SYSTEM =====

// Register for an activity
export const registerForActivity = async (registrationId, userId, userData) => {
  try {
    // Use transaction to ensure data consistency
    const result = await runTransaction(db, async (transaction) => {
      // Check if user has already registered
      const existingRegistrationQuery = query(
        collection(db, COLLECTIONS.ACTIVITY_PARTICIPANTS),
        where('registrationId', '==', registrationId),
        where('userId', '==', userId)
      );
      const existingRegistrationSnapshot = await getDocs(existingRegistrationQuery);
      
      if (!existingRegistrationSnapshot.empty) {
        throw new Error('User has already registered for this activity');
      }

      // Check if registration is still open
      const registrationRef = doc(db, COLLECTIONS.ACTIVITY_REGISTRATIONS, registrationId);
      const registrationSnap = await transaction.get(registrationRef);
      
      if (!registrationSnap.exists()) {
        throw new Error('Activity registration not found');
      }
      
      const registrationData = registrationSnap.data();
      if (!registrationData.isActive) {
        throw new Error('Registration is not active');
      }
      
      const now = new Date();
      const startTime = new Date(registrationData.registrationStartTime);
      const endTime = new Date(registrationData.registrationEndTime);
      
      if (now < startTime) {
        throw new Error('Registration has not started yet');
      }
      
      if (now > endTime) {
        throw new Error('Registration has ended');
      }

      // Check capacity
      if (registrationData.totalRegistrations >= registrationData.maxParticipants) {
        throw new Error('Registration is full');
      }

      // Add participant registration
      const participantRef = doc(collection(db, COLLECTIONS.ACTIVITY_PARTICIPANTS));
      transaction.set(participantRef, {
        registrationId,
        userId,
        userData,
        registeredAt: serverTimestamp()
      });

      // Update registration participant count
      transaction.update(registrationRef, {
        totalRegistrations: registrationData.totalRegistrations + 1,
        updatedAt: serverTimestamp()
      });

      return { success: true };
    });

    return result;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get participants for an activity
export const getActivityParticipants = async (registrationId) => {
  try {
    const participantsQuery = query(
      collection(db, COLLECTIONS.ACTIVITY_PARTICIPANTS),
      where('registrationId', '==', registrationId)
    );
    const snapshot = await getDocs(participantsQuery);
    
    const participants = [];
    snapshot.forEach((doc) => {
      participants.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, data: participants };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Listen to activity participants in real-time
export const listenToActivityParticipants = (registrationId, callback) => {
  const participantsQuery = query(
    collection(db, COLLECTIONS.ACTIVITY_PARTICIPANTS),
    where('registrationId', '==', registrationId)
  );

  const unsubscribe = onSnapshot(participantsQuery, (snapshot) => {
    const participants = [];
    snapshot.forEach((doc) => {
      participants.push({ id: doc.id, ...doc.data() });
    });
    callback({ success: true, data: participants });
  }, (error) => {
    callback({ success: false, error: error.message });
  });

  return unsubscribe;
};

// ===== REGISTRATION ANALYTICS =====

// Get registration statistics
export const getActivityRegistrationStats = async (registrationId) => {
  try {
    const participantsQuery = query(
      collection(db, COLLECTIONS.ACTIVITY_PARTICIPANTS),
      where('registrationId', '==', registrationId)
    );
    const participantsSnapshot = await getDocs(participantsQuery);
    
    const participants = [];
    participantsSnapshot.forEach((doc) => {
      participants.push({ id: doc.id, ...doc.data() });
    });

    // Get registration data
    const registrationRef = doc(db, COLLECTIONS.ACTIVITY_REGISTRATIONS, registrationId);
    const registrationSnap = await getDoc(registrationRef);
    
    if (!registrationSnap.exists()) {
      return { success: false, error: 'Registration not found' };
    }

    const registrationData = registrationSnap.data();
    
    // Calculate statistics
    const stats = {
      totalRegistrations: participants.length,
      maxParticipants: registrationData.maxParticipants,
      fillRate: Math.round((participants.length / registrationData.maxParticipants) * 100),
      registrationStartTime: registrationData.registrationStartTime,
      registrationEndTime: registrationData.registrationEndTime,
      activityDate: registrationData.activityDate,
      isActive: registrationData.isActive,
      participants: participants
    };

    return { 
      success: true, 
      data: stats
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ===== ADMIN FUNCTIONS =====

// Close activity registration (admin only)
export const closeActivityRegistration = async (registrationId) => {
  try {
    const registrationRef = doc(db, COLLECTIONS.ACTIVITY_REGISTRATIONS, registrationId);
    await updateDoc(registrationRef, {
      isActive: false,
      closedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Extend registration period (admin only)
export const extendRegistrationPeriod = async (registrationId, newEndTime) => {
  try {
    const registrationRef = doc(db, COLLECTIONS.ACTIVITY_REGISTRATIONS, registrationId);
    await updateDoc(registrationRef, {
      registrationEndTime: newEndTime,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ===== UTILITY FUNCTIONS =====

// Check if registration is open
export const isRegistrationOpen = (registration) => {
  if (!registration.isActive) return false;
  const now = new Date();
  const startTime = new Date(registration.registrationStartTime);
  const endTime = new Date(registration.registrationEndTime);
  return now >= startTime && now <= endTime;
};

// Check if registration is expired
export const isRegistrationExpired = (registration) => {
  const now = new Date();
  const endTime = new Date(registration.registrationEndTime);
  return now > endTime;
};

// Get time remaining for registration
export const getRegistrationTimeRemaining = (endTime) => {
  if (!endTime) return null;
  const now = new Date().getTime();
  const end = new Date(endTime).getTime();
  return Math.max(0, end - now);
};

export default {
  // Registration management
  createActivityRegistration,
  updateActivityRegistration,
  deleteActivityRegistration,
  getActivityRegistration,
  getAllActivityRegistrations,
  
  // Real-time listeners
  listenToActivityRegistration,
  listenToAllActivityRegistrations,
  
  // Participant registration
  registerForActivity,
  getActivityParticipants,
  listenToActivityParticipants,
  
  // Analytics
  getActivityRegistrationStats,
  
  // Admin functions
  closeActivityRegistration,
  extendRegistrationPeriod,
  
  // Utilities
  isRegistrationOpen,
  isRegistrationExpired,
  getRegistrationTimeRemaining
};
